/* eslint-disable react/no-unused-prop-types,no-console,max-len */
import React from 'react';
import PropTypes from 'prop-types';
import Moment from 'react-moment';
import ReactTable from 'react-table';
import ContentLoader from 'react-content-loader';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
// material-ui icons
import Done from '@material-ui/icons/Done';
import Check from '@material-ui/icons/Check';
import TextField from '@material-ui/core/TextField';
import Close from '@material-ui/icons/Clear';
import ArrowForward from '@material-ui/icons/ArrowForward';
import LinearProgress from '@material-ui/core/LinearProgress';
import FilterIco from '@material-ui/icons/FilterList';
import Refresh from '@material-ui/icons/Refresh';
// material-ui/ core components
import extendedTablesStyle from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle';
import Checkbox from '@material-ui/core/Checkbox';
import { Hidden } from '@material-ui/core';
import withStyles from '@material-ui/core/styles/withStyles';
// core components
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
import Snackbar from 'components/UI/Snackbar/Snackbar';
// global variables
import { promotedAdPackagesStatus, locationScopes } from 'config';
// our components
import RouterContext from 'helpers/routerContext';
import { LoadingAnimation } from 'components/common/Loading';
import Paginator from 'components/common/Paginator';
import { TableLoader } from 'components/common/TableLoader';
import RedirectFlashMessage from 'components/common/RedirectFlashMessage';
import Filter from 'components/PromotedAdPackages/Filter';
import WithPermission from 'components/User/WithPermission';
import { defaultFilters } from 'components/PromotedAdPackages/List';
import ModalConfirmation from 'components/common/ModalConfirmation';

/**
 * @class
 * @extends React.Component
 */
class PromotedAdPackagesList extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      selectedItems: [],
      showFilters: false,
      currentAction: undefined,
      modalIsOpen: false,
      updatePrice: undefined,
    };
  }

  /**
   * @description
   * Select all items.
   *
   * @param {Array} edges - All ads from api.
   * @returns {undefined} No return.
   */
  handleSelectAll = edges => {
    const { selectedItems } = this.state;
    // if all are selected
    if (selectedItems.length === edges.length) {
      this.setState({ selectedItems: [] });
    } else {
      this.setState({ selectedItems: edges.map(item => item.node.id) });
    }
  };

  /**
   * @description
   * Select an item.
   *
   * @param {number} id - Id of selected item.
   * @returns {undefined} No return.
   */
  handleSelectItem = id => {
    const { selectedItems } = this.state;
    // if item is selected
    if (selectedItems.indexOf(id) !== -1) {
      this.setState({
        selectedItems: selectedItems.filter(item => item !== id),
      });
    } else {
      this.setState({ selectedItems: [...selectedItems, id] });
    }
  };

  /**
   * @description
   * Open destroying modal.
   *
   * @param {string} action - Action.
   */
  handleOpenModal = action => {
    this.setState({
      modalIsOpen: true,
      currentAction: action,
    });
  };

  /**
   * @description
   * Callback to close destroying modal if user close it manually.
   */
  handleCloseModal = () => {
    this.setState({
      modalIsOpen: false,
      currentAction: '',
    });
  };

  /**
   * @description
   * Confirm delete callback.
   * @returns {undefined} No return.
   *
   */
  handleSubmit = () => {
    const { selectedItems, currentAction, updatePrice } = this.state;
    const { packageUpdate, packageDisable } = this.props;

    if (currentAction === 'update') {
      packageUpdate(selectedItems, updatePrice);
    } else {
      packageDisable(selectedItems);
    }

    this.setState({
      selectedItems: [],
      modalIsOpen: false,
      currentAction: '',
      updatePrice: undefined,
    });
  };

  /**
   * @description
   * Handle show collapsed or full filters.
   */
  handleShowFilters = () => {
    const { showFilters } = this.state;
    this.setState({ showFilters: !showFilters });
  };

  /**
   * @description
   * Format frequency and duration received from the server.
   *
   * @param {Object} param - Frequency or duration received from the server.
   * @param {Object} i18n - Translation.
   * @returns {Object} Converted and translated frequency and duration.
   */
  changeFormatToString = (
    { years, months, days, hours, minutes, seconds },
    i18n,
  ) => {
    moment.locale(i18n.language);
    return moment()
      .add({ seconds, minutes, hours, days, months, years })
      .fromNow(true);
  };

  /**
   * @description
   * Render Status cell.
   *
   * @param {Object} node - An ad.
   * @returns {*} Status cell.
   */
  renderCellStatus = node => {
    const { classes, t } = this.props;

    switch (node.status) {
      case promotedAdPackagesStatus.ENABLED:
        return (
          <h6 className={`${classes.cellStatus} ${classes.runningTag}`}>
            {t(
              `promoted_ad_packages.list.status.${promotedAdPackagesStatus.ENABLED}`,
            )}
          </h6>
        );
      case promotedAdPackagesStatus.DISABLED:
        return (
          <h6 className={`${classes.cellStatus} ${classes.deletedTag}`}>
            {t(
              `promoted_ad_packages.list.status.${promotedAdPackagesStatus.DISABLED}`,
            )}
          </h6>
        );
      default:
        break;
    }
    return null;
  };

  /**
   * @description
   * To give a row color depending of column status.
   *
   * @param {number} nodeId - Id of an Ad.
   * @returns {string} Row color.
   */
  renderRowColor = nodeId => {
    const { data } = this.props;
    const node = data.promotedAdPackages.edges.find(
      ({ node: { id } }) => id === nodeId,
    );
    switch (node.node.status) {
      case promotedAdPackagesStatus.DISABLED:
        return 'rgba(175, 34, 58, .1)';
      default:
        return '';
    }
  };

  /**
   * @description
   * Description of ReactTable columns.
   *
   * @param {Array} edges - All ads from api.
   * @returns {Array} Table columns.
   */
  renderColumns = edges => {
    const { selectedItems } = this.state;
    const { classes, t } = this.props;
    return [
      {
        Header: (
          <Checkbox
            onClick={() => this.handleSelectAll(edges)}
            checked={edges && selectedItems.length === edges.length}
            id="allCheckbox"
            checkedIcon={<Check className={classes.checkedIcon} />}
            icon={<Check className={classes.uncheckedIcon} />}
            classes={{
              checked: classes.checked,
            }}
          />
        ),
        accessor: 'checkbox',
        sortable: false,
        filterable: false,
        maxWidth: 58,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('promoted_ad_packages.list.headers.id')}
          </span>
        ),
        accessor: 'id',
        sortable: false,
        filterable: false,
        minWidth: 90,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('promoted_ad_packages.list.headers.subcategory')}
          </span>
        ),
        accessor: 'subcategory',
        sortable: false,
        filterable: false,
        minWidth: 90,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('promoted_ad_packages.list.headers.location_scope')}
          </span>
        ),
        accessor: 'location_scope',
        sortable: false,
        filterable: false,
        minWidth: 90,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('promoted_ad_packages.list.headers.duration')}
          </span>
        ),
        accessor: 'duration',
        sortable: false,
        filterable: false,
        minWidth: 90,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('promoted_ad_packages.list.headers.province')}
          </span>
        ),
        accessor: 'province',
        sortable: false,
        filterable: false,
        minWidth: 90,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('promoted_ad_packages.list.headers.municipality')}
          </span>
        ),
        accessor: 'municipality',
        sortable: false,
        filterable: false,
        minWidth: 90,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('promoted_ad_packages.list.headers.price')}
          </span>
        ),
        accessor: 'price',
        sortable: false,
        filterable: false,
        minWidth: 90,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('promoted_ad_packages.list.headers.date')}
          </span>
        ),
        accessor: 'date',
        sortable: false,
        filterable: false,
        minWidth: 90,
      },
      {
        Header: (
          <span className={classes.headerField} style={{ textAlign: 'center' }}>
            <p>{t('promoted_ad_packages.list.headers.status')}</p>
          </span>
        ),
        accessor: 'status',
        sortable: false,
        filterable: false,
        minWidth: 100,
      },
      {
        expander: true,
        show: false,
      },
    ];
  };

  /**
   * @description
   * Format the DATE received from the server.
   *
   * @param {string} date - DATE received from the server.
   * @returns {Object} Converted date.
   */
  dateFormat = date => {
    const calendarStrings = {
      lastDay: '[Ayer ] LT',
      sameDay: '[Hoy ] LT',
      lastWeek: '[] dddd[, ] LT',
      sameElse: 'L',
    };
    // 2019-02-08T15:59-0500 date for testing
    return <Moment date={date} calendar={calendarStrings} />;
  };

  /**
   * @description
   * Map the data from api to ReactTable rows.
   *
   * @param {Array} edges - All ads from api.
   * @returns {Array} Table rows.
   */
  renderRows = edges => {
    const { selectedItems } = this.state;
    const { classes, i18n } = this.props;
    return edges.map(({ node }) => ({
      id: node.id,
      checkbox: (
        <Checkbox
          onClick={() => {
            this.handleSelectItem(node.id);
          }}
          checked={selectedItems.indexOf(node.id) !== -1}
          checkedIcon={<Check className={classes.checkedIcon} />}
          icon={<Check className={classes.uncheckedIcon} />}
          classes={{
            checked: classes.checked,
          }}
        />
      ),
      subcategory: (
        <ul
          id={`package-subcategory-${node.id}`}
          key={`package-subcategory-${node.id}`}
          className={classes.adCatBread}
        >
          <li data-cy="packageCategory">
            {node.subcategory.parentCategory.title}
          </li>
          <li className={classes.adIconBread}>
            <ArrowForward color="disabled" />
          </li>
          <li data-cy="adSubcategory">{node.subcategory.title}</li>
        </ul>
      ),
      location_scope: <span data-cy="locationScope">{node.locationScope}</span>,
      duration: (
        <span className={classes.paidAdDuration}>
          {this.changeFormatToString(node.duration, i18n)}
        </span>
      ),
      province: (
        <span data-cy="province">
          {node.locationScope !== locationScopes.NATIONAL
            ? node.province.name
            : ''}
        </span>
      ),
      municipality: (
        <span data-cy="municipality">
          {node.locationScope === locationScopes.MUNICIPAL
            ? node.municipality.name
            : ''}
        </span>
      ),
      price: <span data-cy="price">{`${node.price} USD`}</span>,
      date: <span data-cy="date">{this.dateFormat(node.updatedOn)}</span>,
      status: <span data-cy="status">{this.renderCellStatus(node)}</span>,
    }));
  };

  /**
   * @description
   * Map Loading index Rows with itemProcessing.
   *
   * @param {Object} itemProcessing - Ids of items that is processing.
   * @param {Object} edges - Edges of connection data.
   */
  mapLoadingRows = (itemProcessing, edges) => {
    const { currentAction } = this.state;
    const loadingRows = {};
    edges.forEach((value, index) => {
      if (currentAction === 'review' || currentAction === 'publish') {
        if (itemProcessing.includes(value.node.ad.id)) {
          loadingRows[index] = true;
        }
      } else if (itemProcessing.includes(value.node.id)) {
        loadingRows[index] = true;
      }
    });
    return loadingRows;
  };

  /**
   * @description
   * Render Filter, Paginator and ReactTable components.
   * @returns {*} Filter, Paginator and ReactTable components.
   */
  render() {
    const {
      data,
      loading,
      fullLoading,
      changeNumberOfRows,
      changePage,
      numberOfRowsSelected,
      paginationDirection,
      classes,
      filter,
      restartFilters,
      appliedFilter,
      itemProcessing,
      refresh,
      t,
      failedIds,
      notification,
      closeNotification,
      createPackagePage,
    } = this.props;
    const {
      selectedItems,
      showFilters,
      modalIsOpen,
      currentAction,
      updatePrice,
    } = this.state;
    const edges =
      loading || data === undefined ? [] : data.promotedAdPackages.edges;
    const rows = this.renderRows(edges);
    const cols = this.renderColumns(edges);
    const loadingRows = this.mapLoadingRows(itemProcessing, edges);
    return (
      <React.Fragment>
        <RedirectFlashMessage />
        <Snackbar
          place="tc"
          color={notification.type === 'success' ? 'success' : 'danger'}
          message={Object.keys(notification).length > 0 && notification.message}
          open={Object.keys(notification).length > 0 && notification.message}
          closeNotification={closeNotification}
          close
        />

        <ModalConfirmation
          type={currentAction === 'update' ? 'warning' : 'danger'}
          show={modalIsOpen}
          confirm={() => this.handleSubmit()}
          cancel={() => this.handleCloseModal()}
          title={
            currentAction === 'update'
              ? t('promoted_ad_packages.list.updateTitle')
              : t('promoted_ad_packages.list.disableTitle')
          }
          description={
            currentAction === 'update'
              ? t('promoted_ad_packages.list.updateDescription')
              : t('promoted_ad_packages.list.disableDescription')
          }
          confirmLabel={
            currentAction === 'update'
              ? t('promoted_ad_packages.list.updateConfirmLabel')
              : t('promoted_ad_packages.list.disableConfirmLabel')
          }
        >
          {currentAction === 'update' && (
            <TextField
              label={t('promoted_ad_packages.form.fields.price')}
              name="price"
              InputProps={{
                value: updatePrice,
                onChange: e => {
                  this.setState({
                    updatePrice: e.target.value,
                  });
                },
                type: 'number',
              }}
            />
          )}
        </ModalConfirmation>

        <GridContainer>
          <GridItem xs={12}>
            <div className={classes.pageContainer}>
              <GridContainer justify="space-between" alignItems="center">
                <GridItem>
                  <h3 className={classes.title}>
                    {t('promoted_ad_packages.list.title')}
                  </h3>
                </GridItem>

                <GridItem>
                  <Button
                    color="primary"
                    simple
                    className={classes.showFilters}
                    onClick={() => refresh()}
                    id="refresh"
                    key="refresh"
                    style={{
                      marginRight: 24,
                    }}
                  >
                    <Refresh />
                    {t('common.refresh')}
                  </Button>
                  <WithPermission allowPermissions={['add_promotedadpackage']}>
                    <Button
                      data-cy="createPackage"
                      color="success"
                      default
                      onClick={createPackagePage}
                      id="createPackage"
                    >
                      {t('promoted_ad_packages.create')}
                    </Button>
                  </WithPermission>
                </GridItem>
              </GridContainer>

              <GridContainer>
                <GridItem xs={12}>
                  <div className={classes.contentContainer}>
                    <div
                      className={[
                        classes.loadingLineContainer,
                        classes.topLine,
                      ].join(' ')}
                    >
                      {' '}
                      {fullLoading && (
                        <LinearProgress
                          classes={{
                            barColorPrimary: classes.loadingLine,
                          }}
                        />
                      )}
                    </div>
                    <GridContainer
                      justify="space-between"
                      alignItems="center"
                      className={[
                        classes.loteActionsAnimation,
                        selectedItems.length > 0 ? classes.hideFilters : '',
                      ].join(' ')}
                    >
                      <GridItem xs={12} sm={8} md={7}>
                        <div className={classes.searchFieldContainer}>
                          <Button
                            color="info"
                            round
                            simple={
                              !showFilters &&
                              JSON.stringify(appliedFilter) ===
                                JSON.stringify(defaultFilters)
                            }
                            // className={classes.filterBtn}
                            onClick={this.handleShowFilters}
                            id="filter"
                            key="filter"
                            data-cy="filterBtn"
                          >
                            <FilterIco />
                            <span>
                              {JSON.stringify(appliedFilter) ===
                              JSON.stringify(defaultFilters)
                                ? t('user.list.filter')
                                : t('user.list.filtering')}
                            </span>
                          </Button>
                        </div>
                        <Hidden
                          only={['sm', 'md', 'lg', 'xl']}
                          implementation="css"
                        >
                          <GridContainer>
                            <GridItem xs={12}>
                              <div
                                className={`${classes.loteActionsAnimation}
                    ${
                      showFilters
                        ? classes.showPaidAdFilters
                        : classes.hideFilters
                    }`}
                              >
                                <Filter
                                  filter={filter}
                                  appliedFilter={appliedFilter}
                                  restartFilters={restartFilters}
                                  showFilters={this.handleShowFilters}
                                />
                              </div>
                            </GridItem>
                          </GridContainer>
                        </Hidden>
                      </GridItem>
                      <GridItem xs={12} sm={4} md={5}>
                        {!fullLoading ? (
                          <Paginator
                            pageInfo={data.promotedAdPackages.pageInfo}
                            changePage={changePage}
                            changeNumberOfRows={changeNumberOfRows}
                            paginationDirection={paginationDirection}
                            numberOfRowsSelected={numberOfRowsSelected}
                          />
                        ) : (
                          <ContentLoader height={67} width={327} speed={2}>
                            <path
                              // eslint-disable-next-line max-len
                              d="M199,16 L304,16 C307.313708,16 310,18.6862915 310,22 L310,46 C310,49.3137085 307.313708,52 304,52 L199,52 C195.686292,52 193,49.3137085 193,46 L193,22 C193,18.6862915 195.686292,16 199,16 Z M25.9861352,27.0121317 L70.0138648,27.0121317 C74.4244855,27.0121317 78,30.5876462 78,34.9982669 C78,39.4088876 74.4244855,42.9844021 70.0138648,42.9844021 L25.9861352,42.9844021 C21.5755145,42.9844021 18,39.4088876 18,34.9982669 C18,30.5876462 21.5755145,27.0121317 25.9861352,27.0121317 Z M103.986135,27.0121317 L148.013865,27.0121317 C152.424485,27.0121317 156,30.5876462 156,34.9982669 C156,39.4088876 152.424485,42.9844021 148.013865,42.9844021 L103.986135,42.9844021 C99.5755145,42.9844021 96,39.4088876 96,34.9982669 C96,30.5876462 99.5755145,27.0121317 103.986135,27.0121317 Z"
                              fill="#D8D8D8"
                            />
                          </ContentLoader>
                        )}
                      </GridItem>
                    </GridContainer>
                    <Hidden only={['xs']} implementation="css">
                      <GridContainer>
                        <GridItem xs={12}>
                          <div
                            className={`${classes.loteActionsAnimation}
                    ${
                      showFilters
                        ? classes.showPaidAdFilters
                        : classes.hideFilters
                    }`}
                          >
                            <Filter
                              filter={filter}
                              appliedFilter={appliedFilter}
                              restartFilters={restartFilters}
                              showFilters={this.handleShowFilters}
                            />
                          </div>
                        </GridItem>
                      </GridContainer>
                    </Hidden>
                    <div
                      className={[
                        classes.groupActions,
                        classes.loteActionsAnimation,
                        selectedItems.length > 0
                          ? classes.showLote
                          : classes.hideLote,
                      ].join(' ')}
                    >
                      <WithPermission
                        allowPermissions={['change_promotedadpackage']}
                      >
                        <Button
                          data-cy="packageUpdate"
                          color="primary"
                          simple
                          onClick={() => this.handleOpenModal('update')}
                          id="packageUpdate"
                        >
                          <Done />
                          {t('common.edit')}
                        </Button>
                        <Button
                          data-cy="deactivatePackage"
                          color="danger"
                          simple
                          onClick={() => this.handleOpenModal('disable')}
                          id="deactivatePackage"
                        >
                          <Close />
                          {t('common.disable')}
                        </Button>
                      </WithPermission>
                    </div>
                    <GridContainer>
                      <GridItem xs={12}>
                        <div>
                          {loading ? (
                            <TableLoader />
                          ) : (
                            <ReactTable
                              data={rows}
                              filterable
                              columns={cols}
                              loading={loading}
                              pageSize={
                                edges.length < numberOfRowsSelected
                                  ? edges.length
                                  : numberOfRowsSelected
                              }
                              showPagination={false}
                              className={[
                                '-striped -highlight',
                                classes.tbl,
                              ].join(' ')}
                              expanded={loadingRows}
                              defaultFilterMethod={(field, row) => {
                                const id = field.pivotId || field.id;
                                return row[id] !== undefined
                                  ? String(row[id]).startsWith(field.value)
                                  : true;
                              }}
                              SubComponent={() => (
                                <div className={classes.loading}>
                                  <LoadingAnimation />
                                </div>
                              )}
                              getTrProps={(state, rowInfo) => ({
                                style: {
                                  backgroundColor:
                                    rowInfo &&
                                    (selectedItems.indexOf(
                                      rowInfo.original.id,
                                    ) !== -1
                                      ? 'rgba(171, 221, 250, .8)'
                                      : this.renderRowColor(
                                          rowInfo.original.id,
                                        )),
                                  borderLeft:
                                    rowInfo &&
                                    failedIds.indexOf(rowInfo.original.id) !==
                                      -1
                                      ? '4px solid red'
                                      : '',
                                },
                              })}
                              noDataText={t('common.no_data')}
                              minRows={8}
                            />
                          )}
                        </div>
                      </GridItem>
                    </GridContainer>
                    <GridContainer justify="flex-end">
                      <GridItem xs={12} sm={4}>
                        {!fullLoading ? (
                          <Paginator
                            pageInfo={data.promotedAdPackages.pageInfo}
                            changePage={changePage}
                            changeNumberOfRows={changeNumberOfRows}
                            paginationDirection={paginationDirection}
                            numberOfRowsSelected={numberOfRowsSelected}
                          />
                        ) : (
                          <ContentLoader height={67} width={327} speed={2}>
                            <path
                              // eslint-disable-next-line max-len
                              d="M199,16 L304,16 C307.313708,16 310,18.6862915 310,22 L310,46 C310,49.3137085 307.313708,52 304,52 L199,52 C195.686292,52 193,49.3137085 193,46 L193,22 C193,18.6862915 195.686292,16 199,16 Z M25.9861352,27.0121317 L70.0138648,27.0121317 C74.4244855,27.0121317 78,30.5876462 78,34.9982669 C78,39.4088876 74.4244855,42.9844021 70.0138648,42.9844021 L25.9861352,42.9844021 C21.5755145,42.9844021 18,39.4088876 18,34.9982669 C18,30.5876462 21.5755145,27.0121317 25.9861352,27.0121317 Z M103.986135,27.0121317 L148.013865,27.0121317 C152.424485,27.0121317 156,30.5876462 156,34.9982669 C156,39.4088876 152.424485,42.9844021 148.013865,42.9844021 L103.986135,42.9844021 C99.5755145,42.9844021 96,39.4088876 96,34.9982669 C96,30.5876462 99.5755145,27.0121317 103.986135,27.0121317 Z"
                              id="Combined-Shape"
                              fill="#D8D8D8"
                            />
                          </ContentLoader>
                        )}
                      </GridItem>
                    </GridContainer>
                  </div>
                </GridItem>
              </GridContainer>
            </div>
          </GridItem>
        </GridContainer>
      </React.Fragment>
    );
  }
}

/**
 * PromotedAdPackagesList PropTypes
 * @param classes - prop styles received from withStyles
 * @param data - prop ads query's results
 * @param itemProcessing - id of items that is processing
 * @param numberOfRowsSelected - prop current number of rows selected
 * @param appliedFilter - prop pass to Filter
 * @param loading - prop ads query or mutations status
 * @param t - prop from HOC with string translations
 * @param i18n - prop from HOC with string translations
 * @param failedIds - prop from HOC with string translations
 * @param notification - prop to know if notification will be showed or not
 * @callback packageUpdate - callback to update elements
 * @callback packageDisable - callback to disable elements
 * @callback changePage - prop pass to Paginator
 * @callback changeNumberOfRows - prop pass to Paginator
 * @callback filter - prop pass to Filter
 * @callback restartFilters - prop pass to Filter
 * @callback createPackagePage - prop pass to List
 * @callback refresh - callback to refresh page
 * @callback closeNotification - callback to close notification
 */

PromotedAdPackagesList.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  itemProcessing: PropTypes.instanceOf(Array).isRequired,
  data: PropTypes.shape({}).isRequired,
  numberOfRowsSelected: PropTypes.number.isRequired,
  // eslint-disable-next-line react/require-default-props
  paginationDirection: PropTypes.string,
  packageUpdate: PropTypes.func.isRequired,
  packageDisable: PropTypes.func.isRequired,
  changePage: PropTypes.func.isRequired,
  changeNumberOfRows: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  fullLoading: PropTypes.bool.isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  filter: PropTypes.func.isRequired,
  restartFilters: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.instanceOf(Object).isRequired,
  refresh: PropTypes.func.isRequired,
  failedIds: PropTypes.instanceOf(Array).isRequired,
  notification: PropTypes.instanceOf(Object).isRequired,
  closeNotification: PropTypes.func.isRequired,
  createPackagePage: PropTypes.func.isRequired,
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to PermissionList component
// This is a small hack, with intermediary Component to resolved it temporally
const PromotedAdPackageWrapper = props => <PromotedAdPackagesList {...props} />;

export default withTranslation()(
  withStyles(extendedTablesStyle)(PromotedAdPackageWrapper),
);
